import { action, computed, makeObservable, observable, toJS } from 'mobx';

import type { TRawRigOperationStage } from 'src/services/wells-service/types';
import { getRandomNumber } from 'src/shared/utils/get-random-number';

import {
  BRIGADE_ID_KEY,
  RIG_ID_KEY,
  RIG_OPERATION_ID_KEY,
  RIG_OPERATION_INDEX_KEY,
  RIG_OPERATION_STAGES_KEY,
} from './consts';
import type { RigOperationStage } from './rig-operation-stage.entity';
import { isRigOperationField } from './rig-operations-service';
import type { TRawRigOperation } from './types';

export type TRigOperationData = {
  rigId: number | null;
  brigadeId: number | null;
  index: number;
};

type TErrors = Partial<Record<keyof TRigOperationData, string>>;

export const RIG_OPERATION_DATA_FIELDS: string[] & (keyof TRigOperationData)[] = ['rigId', 'brigadeId', 'index'];

export class RigOperation {
  readonly entityId = getRandomNumber();
  readonly initialData: Partial<TRigOperationData>;
  readonly initialStages?: TRawRigOperationStage[] = [];
  rawRigOperation?: TRawRigOperation;

  @observable private _errors: TErrors = {};
  @observable rigOperationId?: number;
  @observable private _data: TRigOperationData;
  @observable stages: RigOperationStage[] = [];
  readonly stagesMap = observable.map<number, RigOperationStage>();

  constructor(index: number, initialValues?: TRawRigOperation) {
    this._data = {
      rigId: initialValues?.[RIG_ID_KEY] ?? null,
      brigadeId: initialValues?.[BRIGADE_ID_KEY] ?? null,
      index,
    };
    this.initialData = {
      rigId: initialValues?.[RIG_ID_KEY],
      brigadeId: initialValues?.[BRIGADE_ID_KEY],
      index,
    };
    this.initialStages = initialValues?.[RIG_OPERATION_STAGES_KEY];
    this.rawRigOperation = initialValues;
    this.rigOperationId = initialValues?.[RIG_OPERATION_ID_KEY];

    makeObservable(this);
  }

  @computed
  get data(): TRigOperationData {
    return toJS(this._data);
  }

  @computed
  get errors(): TErrors {
    return toJS(this._errors);
  }

  @action.bound
  updateData(rawRigOperation: TRawRigOperation): void {
    this.rigOperationId = rawRigOperation[RIG_OPERATION_ID_KEY];
    this.rawRigOperation = rawRigOperation;
    this._data = {
      ...this._data,
      rigId: rawRigOperation[RIG_ID_KEY] ?? null,
      brigadeId: rawRigOperation[BRIGADE_ID_KEY] ?? null,
      index: rawRigOperation[RIG_OPERATION_INDEX_KEY],
    };
  }

  @action.bound
  clearErrors(): void {
    this._errors = {};
  }

  @action.bound
  addError(fieldId: keyof TRigOperationData, errorText: string): void {
    this._errors = { ...this._errors, [fieldId]: errorText };
  }

  @action.bound
  removeError(fieldId: keyof TRigOperationData): void {
    const errors: TErrors = {};

    for (const key in this._errors) {
      if (key === fieldId || !isRigOperationField(key)) {
        continue;
      }

      errors[key] = this._errors[key];
    }

    this._errors = errors;
  }

  @action.bound
  setStages(stages: RigOperationStage[]): void {
    this.stages = stages;

    this.stagesMap.clear();

    stages.forEach((s) => this.stagesMap.set(s.entityId, s));
  }

  @computed
  get rigId(): number | null {
    return this._data.rigId;
  }

  @action.bound
  setRigId(id: number | null): void {
    this._data = {
      ...this._data,
      rigId: id,
    };
  }

  @action.bound
  setRigOperationId(id: number): void {
    this.rigOperationId = id;
  }

  @computed
  get brigadeId(): number | null {
    return this._data.brigadeId;
  }

  @action.bound
  setBrigadeId(id: number | null): void {
    this._data = {
      ...this._data,
      brigadeId: id,
    };
  }

  @computed
  get index(): number {
    return this._data.index;
  }

  @action.bound
  updateIndex(index: number): void {
    this._data = {
      ...this._data,
      index,
    };
  }
}
